import { Clock, MapPin, Phone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Section, SectionHeading } from "../components/Section";
import Reveal from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { LOCATIONS, directionsUrl } from "../lib/content";
import { useHours } from "../hooks/useHours";

export default function Contact() {
  const { hoursFor, loading } = useHours();

  return (
    <>
      <Seo
        title="Contact West Branch & Lake Milton Marinas | Hours & Phone"
        description="Call West Branch Marina at (330) 296-9209 or Lake Milton at (330) 654-4991. Current operating hours come straight from our booking system."
        path="/contact"
        image="/images/courtesy_dock_evening.jpeg"
      />

      <PageHero
        image="/images/courtesy_dock_evening.jpeg"
        alt="Courtesy dock at the launch ramp in the evening"
        eyebrow="Contact"
        title="Talk to the dock"
        subtitle="The fastest way to sort out a booking, a towing question or the weather is a phone call."
      />

      <Section tone="white">
        <SectionHeading
          eyebrow="Two marinas"
          title="Call us directly"
          intro="Hours below are pulled live from our booking system, so they always match what can actually be reserved."
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {Object.values(LOCATIONS).map((loc, i) => {
            const h = hoursFor(loc.slug);
            return (
              <Reveal
                key={loc.slug}
                delay={i * 90}
                data-testid={`contact-card-${loc.slug}`}
                className="rounded-2xl border border-sand-border bg-sand p-7"
              >
                <p className="eyebrow text-lake">{loc.park}</p>
                <h3 className="mt-3 font-display text-2xl font-bold text-navy">{loc.name}</h3>

                <dl className="mt-6 space-y-4 text-sm">
                  <div className="flex gap-3">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
                    <div>
                      <dt className="font-bold uppercase tracking-wider text-slate-500 text-xs">Phone</dt>
                      <dd>
                        <a
                          href={loc.phoneHref}
                          data-testid={`contact-phone-${loc.slug}`}
                          className="font-display text-lg font-bold text-navy hover:text-ember transition-colors"
                        >
                          {loc.phone}
                        </a>
                      </dd>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
                    <div>
                      <dt className="font-bold uppercase tracking-wider text-slate-500 text-xs">Hours</dt>
                      <dd data-testid={`contact-hours-${loc.slug}`} className="text-slate-700">
                        {loading
                          ? "Loading…"
                          : h
                            ? `Daily ${h.open} – ${h.close}`
                            : "Call for this season's hours."}
                      </dd>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-lake" aria-hidden="true" />
                    <div>
                      <dt className="font-bold uppercase tracking-wider text-slate-500 text-xs">Where</dt>
                      <dd className="text-slate-700" data-testid={`contact-address-${loc.slug}`}>
                        {loc.streetAddress}
                        <br />
                        {loc.city}, {loc.region} {loc.postalCode}
                        <span className="mt-1 block text-xs text-slate-500">{loc.park}</span>
                      </dd>
                      <a
                        href={directionsUrl(loc)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`contact-directions-${loc.slug}`}
                        className="mt-2 inline-block text-sm font-bold text-lake hover:underline"
                      >
                        Get directions
                      </a>
                    </div>
                  </div>
                </dl>

                <a
                  href={loc.phoneHref}
                  data-testid={`contact-call-button-${loc.slug}`}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-ember-hover sm:w-auto"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  Call {loc.phone}
                </a>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-12 rounded-2xl border border-sand-border bg-sand p-7">
          <h2 className="font-display text-xl font-semibold text-navy">Before you call</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-700">
            It helps to know your date, roughly how long you want to be out, and whether you plan to tow tubes or skis —
            towing is only permitted on the VIP and deluxe pontoon models. Cancellations, transfers and weather
            rescheduling are all handled over the phone.
          </p>
        </Reveal>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl">
          <ContactForm />
        </div>
      </Section>
    </>
  );
}
